/**
 * GPS calculation utilities for the GPS Hunt application
 * Uses Haversine formula for distance and bearing calculations
 */

const EARTH_RADIUS_METERS = 6371000;

/**
 * Convert degrees to radians
 */
export function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 */
export function toDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Normalize angle to 0-360 range
 */
export function normalizeAngle(angle: number): number {
  return ((angle % 360) + 360) % 360;
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @returns Distance in meters
 */
export function calculateDistance(from: Coordinates, to: Coordinates): number {
  const lat1 = toRadians(from.latitude);
  const lat2 = toRadians(to.latitude);
  const deltaLat = toRadians(to.latitude - from.latitude);
  const deltaLon = toRadians(to.longitude - from.longitude);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
}

/**
 * Calculate initial bearing from one point to another
 * @returns Bearing in degrees (0-360, where 0 = North, 90 = East, etc.)
 */
export function calculateBearing(from: Coordinates, to: Coordinates): number {
  const lat1 = toRadians(from.latitude);
  const lat2 = toRadians(to.latitude);
  const deltaLon = toRadians(to.longitude - from.longitude);

  const y = Math.sin(deltaLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLon);

  const bearing = toDegrees(Math.atan2(y, x));
  return normalizeAngle(bearing);
}

/**
 * Calculate relative bearing (direction to turn from current heading)
 * @param currentHeading Current compass heading in degrees
 * @param targetBearing Bearing to target in degrees
 * @returns Relative bearing in degrees (-180 to 180, where positive = turn right)
 */
export function calculateRelativeBearing(
  currentHeading: number,
  targetBearing: number
): number {
  let diff = targetBearing - currentHeading;
  
  // Normalize to -180 to 180
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  
  return diff;
}

/**
 * Format distance for display
 * @param meters Distance in meters
 * @returns Formatted string (e.g., "45m" or "1.2km")
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

/**
 * Get cardinal direction from bearing
 */
export function getCardinalDirection(bearing: number): string {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(normalizeAngle(bearing) / 45) % 8;
  return directions[index];
}

/**
 * Check if user is within proximity of target
 * @param distance Distance to target in meters
 * @param threshold Proximity threshold in meters (default 10, but app uses 25)
 */
export function isWithinProximity(distance: number, threshold: number = 10): boolean {
  return distance <= threshold;
}

/**
 * Calculate the arrow rotation for compass display
 * @param deviceHeading Current device compass heading
 * @param targetBearing Bearing to target
 * @returns CSS rotation in degrees (for transform: rotate())
 */
export function calculateArrowRotation(
  deviceHeading: number,
  targetBearing: number
): number {
  // The arrow should point toward the target
  // So we rotate by the difference between target bearing and device heading
  return normalizeAngle(targetBearing - deviceHeading);
}

/**
 * Interpolate between two angles for smooth animation
 * Handles wrap-around at 0/360
 */
export function interpolateAngle(
  from: number,
  to: number,
  factor: number
): number {
  let diff = to - from;
  
  // Handle wrap-around
  if (Math.abs(diff) > 180) {
    diff = diff > 0 ? diff - 360 : diff + 360;
  }
  
  return normalizeAngle(from + diff * factor);
}
